import React, { FC, memo, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  EmailIcon,
  Heading,
  IconButton,
  LinkIcon,
  Modal,
  ShareIcon,
  isMobileQuery,
} from '@higo/ui';
import { useCreateShareVisitLinkMutation } from './useCreateShareVisitLinkMutation';

import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { useDispatchDoctorTrackedType } from 'hooks/useDispatchDoctorTrackedEvent';
import {
  DoctorTrackedType,
  TrackedEventButtonClickAction,
} from '@higo/common/lib/features/TrackedEvent';
import useMedia from 'react-use/lib/useMedia';

const ContentContainer = styled.div`
  padding: 2rem;
  width: 12.5625rem;
`;

const StyledHeading = styled(Heading)`
  margin: 0 0 1rem;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ListElement = styled('li')(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: 1rem 1fr;
    grid-column-gap: 1rem;
    align-items: center;
    padding: 0.5rem 0.75rem;

    color: ${theme.palette.secondary['500']};
    ${theme.typography.subtitle1};

    &:hover {
      cursor: pointer;
      background-color: ${theme.palette.neutral[300]};
      border-radius: 0.5rem;
    }
  `,
);

interface ShareVisitModalProps {
  visitId: number;
  examData: any;
}

export const ShareExaminationModalForVisits: FC<ShareVisitModalProps> = memo(
  ({ ...props }: ShareVisitModalProps) => {
    const [visible, setVisible] = useState(false);
    const sendTrackedEvent = useDispatchDoctorTrackedType();

    const { mutateAsync: generateShareLinkForVisit } =
      useCreateShareVisitLinkMutation();
    const intl = useIntl();

    const handleSendEmail = () =>
      generateShareLinkForVisit(props.visitId).then((res) => {
        sendTrackedEvent({
          event: DoctorTrackedType.ButtonClick,
          eventAction: TrackedEventButtonClickAction.ShareExaminationViaEmail,
          examinationId: props.visitId,
        });

        window.location.href = `mailto:?subject=${encodeURIComponent(
          intl.formatMessage({ id: 'share.sendViaEmail.subject' }),
        )}&body=${encodeURIComponent(
          intl.formatMessage(
            { id: 'share.sendViaEmail.body' },
            { link: res.sharePageUrl },
          ),
        )}`;
      });

    const handleCopyLink = () =>
      generateShareLinkForVisit(props.visitId).then((res) => {

        sendTrackedEvent({
          event: DoctorTrackedType.ButtonClick,
          eventAction: TrackedEventButtonClickAction.ShareExaminationViaLink,
          examinationId: props.visitId,
        });

        navigator.clipboard.writeText(res.sharePageUrl).then(() =>
          toast.success(
            intl.formatMessage({
              id: 'share.copyLink.copiedToClipboard',
            }),
          ),
        );
      });

    const isMobileView = useMedia(`${isMobileQuery}`);

    return (
      <>
        <Modal
          visible={visible}
          onClose={() => {
            setVisible(false);
          }}
          fullWidth={isMobileView}
          fullscreen={isMobileView}
        >
          <ContentContainer>
            <StyledHeading level={3}>
              <FormattedMessage id="share.share" />
            </StyledHeading>
            <List>
              <ListElement onClick={handleSendEmail} id="send-via-email-link">
                <EmailIcon />
                <FormattedMessage id="share.sendViaEmail" />
              </ListElement>

              <ListElement onClick={handleCopyLink} id="copy-link">
                <LinkIcon />
                <FormattedMessage id="share.copyLink" />
              </ListElement>
            </List>
          </ContentContainer>
        </Modal>

        <IconButton
          color="secondary"
          size="small"
          icon={<ShareIcon />}
          id="share-button"
          onClick={() => {
            setVisible(true);
            sendTrackedEvent({
              event: DoctorTrackedType.ButtonClick,
              eventAction:
                TrackedEventButtonClickAction.OpenShareExaminationModal,
              examinationId: props.visitId,
            });
          }}
        />
      </>
    );
  },
);
